import { useCallback, useEffect, useRef } from 'react';
import { isOAuthCallbackFrom } from '@/shared/lib/oauth-message';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useConnectPlatform, useDisconnectPlatform } from '@/shared/hooks/usePlatforms';
import { useToast } from '@/shared/components/Toast';
import type { PlatformId } from '@/shared/components/PlatformIcon';
import type { ConnectMethod } from '@/shared/services/platforms.service';
import { errorCodeOf, getApiErrorMessage } from '@/shared/lib/api-errors';
import { confirmDialog } from '@/shared/stores/confirm.store';
import { channelErrorText, oauthCallbackError, platformBrandName } from '@/shared/lib/channel-availability';

/** Brand names are not translated. */
const PLATFORM_NAMES: Record<PlatformId, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'X',
  linkedin: 'LinkedIn',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
  tiktok: 'TikTok',
  google_business: 'Google Business Profile',
  zetpin: 'Zetpin',
};

/** How often to check whether the OAuth popup was closed without finishing. */
const POPUP_POLL_MS = 500;

interface UseChannelOAuthResult {
  /** `method`: the way to connect when the channel offers a choice (Instagram Login, a LinkedIn Company Page). */
  connect: (platformId: PlatformId, method?: ConnectMethod) => Promise<void>;
  disconnect: (platformId: PlatformId) => Promise<void>;
  isConnecting: boolean;
  isDisconnecting: boolean;
}

type OAuthMessage = { type?: string; payload?: { connected?: string; error?: string; code?: string; platform?: string; missing?: string } } | null;

interface UseChannelOAuthOptions {
  /** Called with the platform the verified popup reports connected. */
  onConnected?: (connected: string) => void;
}

/**
 * Encapsulates OAuth connect/disconnect flow with popup handling, message
 * passing, popup-blocker fallback, query invalidation, and toasts.
 *
 * Usage:
 *   const { connect, disconnect, isConnecting } = useChannelOAuth();
 *   <button onClick={() => connect('instagram')}>Connect Instagram</button>
 */
export function useChannelOAuth(options: UseChannelOAuthOptions = {}): UseChannelOAuthResult {
  const onConnectedRef = useRef(options.onConnected);
  onConnectedRef.current = options.onConnected;
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const connectPlatform = useConnectPlatform();
  const disconnectPlatform = useDisconnectPlatform();
  // Tears down the live popup's message listener and close-poll. Called when
  // the callback arrives, when the popup closes without finishing, when a new
  // connect starts, and on unmount, so no listener outlives its popup.
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => {
    cleanupRef.current?.();
    cleanupRef.current = null;
  }, []);

  const connect = useCallback(
    async (platformId: PlatformId, method?: ConnectMethod): Promise<void> => {
      const name = PLATFORM_NAMES[platformId] ?? platformId;

      // The popup is opened now, inside the click: opened after the URL
      // request resolves, Safari, iOS and Firefox block it as unrequested,
      // and the connect falls back to a full-page redirect that leaves the
      // composer or onboarding. It shows a blank page until the URL arrives.
      const w = 600;
      const h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open('about:blank', 'oauth-popup', `width=${w},height=${h},left=${left},top=${top},popup=yes`);
      const closePopup = (): void => {
        try {
          popup?.close();
        } catch {
          // already gone
        }
      };

      let result: { url: string; state: string };
      try {
        result = await connectPlatform.mutateAsync({ platform: platformId, ...(method ? { method } : {}) });
      } catch (err: unknown) {
        closePopup();
        const axiosErr = err as {
          response?: { status?: number; data?: { message?: string } };
          code?: string;
        };
        if (axiosErr.code === 'ERR_NETWORK' || !axiosErr.response) {
          toast('error', t('channels.oauth.offline'));
          return;
        }
        // The API wraps errors as { success: false, error: { code, message } }:
        // a known code (CHANNEL_UNAVAILABLE, LIMIT_REACHED…) gets the app's
        // own translated words.
        toast('error', channelErrorText(errorCodeOf(err), platformId) ?? getApiErrorMessage(err, t('channels.oauth.connectFailed', { name })));
        return;
      }

      let navigated = false;
      if (popup && !popup.closed) {
        try {
          popup.location.href = result.url;
          navigated = true;
        } catch {
          navigated = false;
        }
      }

      // A previous popup's listener must not answer for this one.
      cleanupRef.current?.();

      let pollId: ReturnType<typeof setInterval> | undefined;
      const onMessage = (e: MessageEvent<OAuthMessage>): void => {
        // Only the popup this window opened, from the app's (or API's) origin.
        if (!isOAuthCallbackFrom(e, popup)) return;
        cleanup();
        const payload = e.data?.payload ?? {};
        const { connected } = payload;
        const error = oauthCallbackError(payload);
        if (connected) {
          // The callback names the API enum (GOOGLE_BUSINESS): shown as its brand name.
          toast('success', t('channels.oauth.connected', { name: platformBrandName(connected) }));
          void queryClient.invalidateQueries({ queryKey: ['platform-statuses'] });
          onConnectedRef.current?.(connected);
        } else if (error) {
          toast('error', error);
        }
      };
      const cleanup = (): void => {
        window.removeEventListener('message', onMessage);
        if (pollId !== undefined) clearInterval(pollId);
        pollId = undefined;
        if (cleanupRef.current === cleanup) cleanupRef.current = null;
      };
      window.addEventListener('message', onMessage);
      cleanupRef.current = cleanup;

      if (!popup || popup.closed || !navigated) {
        cleanup();
        closePopup();
        toast('info', t('channels.oauth.popupBlocked', { name }));
        window.location.href = result.url;
        return;
      }

      // Closed without finishing: stop listening. The callback page posts
      // its message before it closes itself, so a real result still lands.
      pollId = setInterval(() => {
        if (popup.closed) cleanup();
      }, POPUP_POLL_MS);
    },
    [connectPlatform, toast, queryClient, t],
  );

  const disconnect = useCallback(
    async (platformId: PlatformId): Promise<void> => {
      const name = PLATFORM_NAMES[platformId] ?? platformId;
      // Destructive: dropping a live connection stops publishing + inbox for
      // this platform. A single mis-click shouldn't do that silently.
      if (!(await confirmDialog({
        title: t('channels.oauth.disconnectConfirm', { name }),
        confirmLabel: t('channels.disconnect'),
      }))) {
        return;
      }
      try {
        await disconnectPlatform.mutateAsync(platformId);
        toast('success', t('channels.oauth.disconnected', { name }));
      } catch (err: unknown) {
        const axiosErr = err as {
          response?: { status?: number; data?: { message?: string } };
          code?: string;
        };
        if (axiosErr.code === 'ERR_NETWORK' || !axiosErr.response) {
          toast('error', t('channels.oauth.offline'));
        } else if (axiosErr.response?.status === 404) {
          toast('warning', t('channels.oauth.alreadyDisconnected', { name }));
          void queryClient.invalidateQueries({ queryKey: ['platform-statuses'] });
        } else {
          // The API wraps errors as { success: false, error: { code, message } }:
          // response.data.message is never set.
          toast('error', getApiErrorMessage(err, t('channels.oauth.disconnectFailed', { name })));
        }
      }
    },
    [disconnectPlatform, toast, queryClient, t],
  );

  return {
    connect,
    disconnect,
    isConnecting: connectPlatform.isPending,
    isDisconnecting: disconnectPlatform.isPending,
  };
}
